import { useState, useMemo, PropsWithChildren } from 'react';
import { styled, css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Popover } from 'radix-ui';
import { TranslationKey } from '../../utils/i18n.ts';
import { useUpMedia } from '../../hooks';
import { useB2CModeStore, B2CMode } from '../../services/store';
import { resetListStyles } from '../../styles/helpers.ts';
import {
  DesktopButton as DefDesktopButton,
  MobileButton as DefMobileButton,
} from '../buttons';
import { IconButton } from '../buttons/IconButton.tsx';
import { Cross } from '../icons';
import { DesktopText, MobileText } from '../texts';

export type ModeMenuProps = PropsWithChildren;

const ModeMenu = ({ children }: ModeMenuProps) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);

  const mode = useB2CModeStore(({ mode }) => mode);
  const changeMode = useB2CModeStore(({ changeMode }) => changeMode);

  const desktop = useUpMedia('md');

  const Title = useMemo(
    () => (desktop ? DesktopTitle : MobileTitle),
    [desktop]
  );

  const Button = useMemo(
    () => (desktop ? DesktopButton : MobileButton),
    [desktop]
  );

  const ButtonText = useMemo(
    () => (desktop ? ButtonDesktopDescText : ButtonMobileDescText),
    [desktop]
  );

  const handleModeChange = (newMode: B2CMode) => {
    changeMode(newMode);
    setOpen(false);
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Popover.Portal>
        <Content>
          <ContentHeader>
            <Title>{t('MODES_POPUP__title')}</Title>
            <Popover.Close asChild aria-label="Close">
              <IconButton color={'silverChalice'}>
                <Cross />
              </IconButton>
            </Popover.Close>
          </ContentHeader>
          <ContentButtonsList>
            {buttons.map(({ mode: m, label }) => {
              const active = m === mode;
              return (
                <ContentButtonsListItem key={m}>
                  {/* @ts-expect-error Button type mismatch */}
                  <Button $active={active} onClick={() => handleModeChange(m)}>
                    {t(label)}
                    {active ? (
                      <>
                        <ButtonText asTag={'span'} $active>
                          {t('MODES_POPUP__connectedButtonText')}
                        </ButtonText>
                      </>
                    ) : (
                      <ButtonText asTag={'span'}>
                        {t('MODES_POPUP__connectButtonText')}
                      </ButtonText>
                    )}
                  </Button>
                </ContentButtonsListItem>
              );
            })}
          </ContentButtonsList>
        </Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

const Content = styled(Popover.Content)`
  min-width: 354px;
  padding: 15px 12px 28px;
  border-radius: 30px;
  background-color: ${({ theme }) => theme.getColor('completelyWhite')};
`;

const ContentHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DesktopTitle = styled(DesktopText).attrs({
  variant: 'h1',
})`
  color: ${({ theme }) => theme.getColor('completelyBlack')};
`;

const MobileTitle = styled(MobileText).attrs({
  variant: 'h1',
})`
  color: ${({ theme }) => theme.getColor('completelyBlack')};
`;

const ContentButtonsList = styled.ul`
  ${resetListStyles};
  display: flex;
  flex-direction: column;
  margin-top: 14px;
`;

const ContentButtonsListItem = styled.li`
  & + & {
    margin-top: 10px;

    ${({ theme }) => theme.getUpMedia('md')} {
      margin-top: ${({ theme }) => theme.responsive(10)};
    }
  }
`;

const commonButtonsStyles = css<CommonButtonsStyles>`
  width: 100%;
  text-align: unset;
  ${({ theme, $active }) =>
    !$active &&
    css`
      background-color: ${theme.getColor('pickledBluewood')};
    `}
  span {
    text-transform: unset;
    display: flex;
    justify-content: space-between;
  }
`;

type CommonButtonsStyles = {
  $active: boolean;
};

const MobileButton = styled(DefMobileButton).attrs({
  size: 'normal',
})<CommonButtonsStyles>`
  ${commonButtonsStyles};
  font-size: 15px;
`;

const DesktopButton = styled(DefDesktopButton)<CommonButtonsStyles>`
  ${commonButtonsStyles};
  font-size: ${({ theme }) => theme.responsive(15)};
`;

const greenDotStyles = css`
  padding-right: 10px;
  &:after {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    margin-left: 4px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.getColor('mountainMeadow')};
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
  }
`;

type CommonButtonDescStyles = {
  $active?: boolean;
};

const ButtonMobileDescText = styled(MobileText)<CommonButtonDescStyles>`
  ${({ $active }) =>
    $active &&
    css`
      position: relative;
      ${greenDotStyles};
    `}
`;

const ButtonDesktopDescText = styled(MobileText)<CommonButtonDescStyles>`
  ${({ $active }) =>
    $active &&
    css`
      position: relative;
      ${greenDotStyles};
    `}
`;

const buttons: ReadonlyArray<{
  mode: B2CMode;
  label: TranslationKey;
}> = [
  {
    mode: 'demo',
    label: 'MODES_POPUP__demoButton',
  },
  {
    mode: 'tournament',
    label: 'MODES_POPUP__tournamentButton',
  },
  {
    mode: 'blockchain',
    label: 'MODES_POPUP__liveModeButton',
  },
];

export { ModeMenu };
